import type { InvestmentExportConverterType, InvestmentImportConverterType, ServiceType } from "@eu.mdotm.api/sphere";
import { UserWithCustomerDtoMfaStatusEnum as MfaStatus } from "@eu.mdotm.api/sphere";
import { atomWithStorage, createJSONStorage, readAtom, useAtomValue } from "@mdotm/shadcn/legacy/reactivity";

// Values MUST match the backend's `Roles` (platform2 …/security/Roles.java) verbatim — the
// server sends these strings uppercase in the user-info payload, so any mismatch silently fails
// every role comparison.
export const UserRole = {
	ROOT: "ROOT",
	ADMIN: "ADMIN",
	MANAGER: "MANAGER",
	GUEST: "GUEST",
} as const;

export type UserRole = (typeof UserRole)[keyof typeof UserRole];

/**
 * The user's MFA state, resolved server-side from `/user/info` (the generated
 * `UserWithCustomerDto.mfaStatus` enum, re-exported under a domain name).
 * - `Enrolled` — a TOTP device is enabled + preferred on the IdP.
 * - `MustEnroll` — the backend requires this user to enroll TOTP before using the
 *   app (it already folds in the role/no-device logic). Drives the forced-enrollment
 *   gate (see `needsMfaEnrollment` in `./mfa`).
 * - `Disabled` — MFA is not required for this user.
 */
export { MfaStatus };

export interface IUser {
	id: string;
	email: string;
	signedIn: boolean;
	name?: string;
	surname?: string;
	customerId?: string;
	availableServices: ServiceType[];
	roles: UserRole[];
	impersonating?: boolean; // Used To check if user is impersonating and disable TrackingService
	customFields?: { [key: string]: object };
	customerName: string;
	importFormats?: Array<InvestmentImportConverterType>;
	exportFormats?: Array<InvestmentExportConverterType>;
	importEnabled?: boolean;
	exportEnabled?: boolean;
	/** User-level decimal places choice (API field: decimalPlaces), bounded by customerDecimalPlacesPreferences. */
	userDecimalPlacesPreferences?: number;
	/** Customer-level ceiling for userDecimalPlacesPreferences (API field: decimalPreference). */
	customerDecimalPlacesPreferences?: number;
	/**
	 * The user's MFA state, resolved server-side from `/user/info`. Left `undefined`
	 * when the backend does not report it, so the enrollment gate stays closed
	 * (fail-safe: only an explicit `MUST_ENROLL` forces enrollment). See `MfaStatus`.
	 */
	mfaStatus?: MfaStatus;
	/**
	 * Whether this user's CUSTOMER may use Sphere AI's web search (API field:
	 * customerWebSearchEnabled — a customer-level setting, served flattened onto the user
	 * info). It is a ceiling, not a preference: the per-chat web-access switch can
	 * only opt out beneath it, never above it, so the composer hides that switch
	 * entirely when this is false rather than offering a toggle that cannot do
	 * anything (see SphereAIComposerSettings).
	 *
	 * Defaults to false when the backend does not report it — withheld rather than
	 * granted, which is also what sphere-mcp does with the same field on its own
	 * copy of the probe. It is the server that actually enforces this; hiding the
	 * switch is only so the UI does not promise what the request will refuse.
	 */
	customerWebSearchEnabled: boolean;
	/**
	 * Whether this user's CUSTOMER distributes its portfolios to SmartX (API field:
	 * customerSmartXEnabled, served flattened onto the user info like the web-search flag).
	 * When false the portfolio page shows no SmartX status and never asks for one.
	 */
	customerSmartXEnabled: boolean;
}

export const guestServices: ServiceType[] = [];
export const guestUser: IUser = {
	id: "",
	email: "",
	signedIn: false,
	availableServices: guestServices,
	customerName: "",
	roles: [],
	customerWebSearchEnabled: false,
	customerSmartXEnabled: false,
	importFormats: undefined,
	customerId: undefined,
	// explicitly empty so that it gets overwritten upon login, ref. auth.ts > updateUser
	userDecimalPlacesPreferences: undefined,
};

/**
 * `user$` at rest — jotai's JSON localStorage adapter with its **cross-tab subscription
 * deliberately left off**.
 *
 * `atomWithStorage`'s default storage also subscribes to the `storage` event, so every write
 * to this key was mirrored, as state, into every other tab of the same browser. That is not a
 * sound thing to share: since MDOT-24702 the credential this identity implies (`accessToken$`)
 * is per-tab and in memory, so a mirrored `signedIn: true` handed a tab an identity it had no
 * token for — it would render the app shell, 401 on everything, and its `unauthorized` handler
 * would answer by logging the *whole browser* out, killing the session the other tab had just
 * signed in for. The reverse write (`signedIn: false`) was just as bad: one tab settling its
 * own signed-out state kicked every other tab off a session that was perfectly alive.
 *
 * Sharing a *session* across tabs is still the right behaviour, and it still happens — but from
 * the credential store both tabs actually agree on (oidc-client-ts's stored `User`), reconciled
 * per tab by `installCrossTabSessionSync`, which hydrates each tab through the same
 * `authSessionInit` a sign-in goes through. Identity is derived there, never copied here.
 *
 * The key itself stays: this atom is still read at boot, and `authSessionInit` still compares
 * the id it finds against the one being signed in to decide whether the per-user state at rest
 * (preferences, notification markers, `userStorage`, the query cache) belongs to this user.
 */
const perTabUserStorage = (() => {
	// Destructured rather than mutated: `createJSONStorage` builds each method as a closure (no
	// `this`), so an object literal keeping only these three is the same adapter minus the
	// `subscribe` jotai would otherwise mount.
	const { getItem, setItem, removeItem } = createJSONStorage<IUser>(() => window.localStorage);
	return { getItem, setItem, removeItem };
})();

export const user$ = atomWithStorage<IUser>("userAtom", guestUser, perTabUserStorage);

export function getCurrentUser(): IUser {
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return readAtom(user$);
}

export function useUserValue(): IUser {
	return useAtomValue(user$);
}

/** The client precision — the customer's decimal places, or 2 for a customer that set none. */
export function customerDecimalPlaces(user: Pick<IUser, "customerDecimalPlacesPreferences">): number {
	return user.customerDecimalPlacesPreferences ?? 2;
}
